/**
 * Central Mongo ids for the three top/revered authors.
 * Import from here anywhere on the site (books, pravachans, legends UI, etc.).
 */
export const JAYADAYALJI_AUTHOR_ID = "5bbc5fa51fd2d735b0087d34";
export const HANUMANPRASADJI_AUTHOR_ID = "5bbc5fcd1fd2d735b0087d35";
export const RAMSUKHDASJI_AUTHOR_ID = "5bbc60101fd2d735b0087d36";

export const JAYADAYALJI_AUTHOR_SLUG = "honorable-shri-jayadayal-ji-goyandka";
export const HANUMANPRASADJI_AUTHOR_SLUG = "honorable-shri-hanuman-prasad-ji-poddar";
export const RAMSUKHDASJI_AUTHOR_SLUG = "honorable-swami-ramsukhdas-ji-maharaj";

/** All top-3 author ids */
export const TOP_AUTHOR_IDS: readonly string[] = [
  JAYADAYALJI_AUTHOR_ID,
  HANUMANPRASADJI_AUTHOR_ID,
  RAMSUKHDASJI_AUTHOR_ID,
];

/** Profile URL slugs for the three top authors */
export const TOP_AUTHOR_SLUGS: readonly string[] = [
  JAYADAYALJI_AUTHOR_SLUG,
  HANUMANPRASADJI_AUTHOR_SLUG,
  RAMSUKHDASJI_AUTHOR_SLUG,
];

/** Map profile URL slug → Mongo id for the three top authors */
export const TOP_AUTHOR_SLUG_TO_ID: Record<string, string> = {
  [JAYADAYALJI_AUTHOR_SLUG]: JAYADAYALJI_AUTHOR_ID,
  [HANUMANPRASADJI_AUTHOR_SLUG]: HANUMANPRASADJI_AUTHOR_ID,
  [RAMSUKHDASJI_AUTHOR_SLUG]: RAMSUKHDASJI_AUTHOR_ID,
};

/** Map Mongo id → profile URL slug (never put id in the address bar) */
export const TOP_AUTHOR_ID_TO_SLUG: Record<string, string> = {
  [JAYADAYALJI_AUTHOR_ID]: JAYADAYALJI_AUTHOR_SLUG,
  [HANUMANPRASADJI_AUTHOR_ID]: HANUMANPRASADJI_AUTHOR_SLUG,
  [RAMSUKHDASJI_AUTHOR_ID]: RAMSUKHDASJI_AUTHOR_SLUG,
};

/** True when authorId or author slug is one of the three top authors */
export const isTopAuthor = (authorIdOrSlug?: string | null): boolean => {
  const value = String(authorIdOrSlug ?? "").trim();
  if (!value) return false;
  return TOP_AUTHOR_IDS.includes(value) || TOP_AUTHOR_SLUGS.includes(value);
};

/** Resolve a top-author slug to Mongo id; pass through ids and unknown values */
export const resolveTopAuthorId = (authorIdOrSlug?: string | null): string => {
  const value = String(authorIdOrSlug ?? "").trim();
  if (!value) return "";
  if (TOP_AUTHOR_IDS.includes(value)) return value;
  return TOP_AUTHOR_SLUG_TO_ID[value] || value;
};

/**
 * Resolve id or slug to the canonical profile slug.
 * Prefer slug in URLs; never expose Mongo id in the path.
 */
export const resolveTopAuthorSlug = (authorIdOrSlug?: string | null): string => {
  const value = String(authorIdOrSlug ?? "").trim();
  if (!value) return "";
  if (TOP_AUTHOR_SLUGS.includes(value)) return value;
  if (TOP_AUTHOR_ID_TO_SLUG[value]) return TOP_AUTHOR_ID_TO_SLUG[value];
  return "";
};

/**
 * Author intro/profile path for desktop + mobile.
 * Always returns `/author/{properSlug}` — never a Mongo id.
 */
export const getAuthorProfilePath = (authorIdOrSlug?: string | null): string => {
  const slug =
    resolveTopAuthorSlug(authorIdOrSlug) || JAYADAYALJI_AUTHOR_SLUG;
  return `/author/${slug}`;
};

/** i18n keys for pravachan page hero title by author id */
export const getPravachanAuthorTitleKey = (authorId?: string | null): string => {
  switch (authorId) {
    case JAYADAYALJI_AUTHOR_ID:
      return "Jayadayal_Ji_pravachan_title";
    case HANUMANPRASADJI_AUTHOR_ID:
      return "HanumanPrasad_Ji_pravachan_title";
    case RAMSUKHDASJI_AUTHOR_ID:
      return "Ramsukhdas_Ji_pravachan_title";
    default:
      return "Pravachan_title_tr";
  }
};

/** i18n keys for pravachan page hero description by author id */
export const getPravachanAuthorDescriptionKey = (authorId?: string | null): string => {
  switch (authorId) {
    case JAYADAYALJI_AUTHOR_ID:
      return "Jayadayal_Ji_pravachan_description";
    case HANUMANPRASADJI_AUTHOR_ID:
      return "HanumanPrasad_Ji_pravachan_description";
    case RAMSUKHDASJI_AUTHOR_ID:
      return "Ramsukhdas_Ji_pravachan_description";
    default:
      return "Pravachan_description_tr";
  }
};
